import { useEffect, useState } from "react";

/**
 * Mirrors `MembershipService.ManualPaymentRetryClaimWindow` on the backend: a second retry for the
 * same membership inside this window is turned away as already in progress, so the retry action
 * stays disabled for the whole window instead of re-enabling the moment a response lands. Change
 * both together.
 */
export const MANUAL_PAYMENT_RETRY_CLAIM_WINDOW_MS = 20_000;

/**
 * Tracks, per membership, until when a pressed "Retry current payment method" action stays
 * disabled. A press starts the cooldown; the backend's in-progress rejection restarts it, because
 * that rejection means the window is still open on the server (another tab, or the retry the
 * backend runs itself after a payment-method update) and the page cannot know how much is left.
 */
export const useManualPaymentRetryCooldown = (windowMs = MANUAL_PAYMENT_RETRY_CLAIM_WINDOW_MS) => {
  const [cooldownUntilByMembershipId, setCooldownUntilByMembershipId] = useState<
    Record<string, number>
  >({});

  // Synchronizes with the wall clock: the claim window closes on its own and nothing in React
  // state changes when it does, so a timer has to wake the render that re-enables the action.
  useEffect(() => {
    const deadlines = Object.values(cooldownUntilByMembershipId);
    if (deadlines.length === 0) {
      return;
    }

    const timeoutId = setTimeout(
      () => {
        setCooldownUntilByMembershipId((current) =>
          Object.fromEntries(Object.entries(current).filter(([, until]) => until > Date.now())),
        );
      },
      Math.max(Math.min(...deadlines) - Date.now(), 0),
    );

    return () => clearTimeout(timeoutId);
  }, [cooldownUntilByMembershipId]);

  return {
    start: (platformUserMembershipId: string) => {
      setCooldownUntilByMembershipId((current) => ({
        ...current,
        [platformUserMembershipId]: Date.now() + windowMs,
      }));
    },
    isActive: (platformUserMembershipId: string) =>
      (cooldownUntilByMembershipId[platformUserMembershipId] ?? 0) > Date.now(),
  };
};
